import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import api, { API } from "../lib/api";

/**
 * Dialog reusable untuk kirim dokumen (Penawaran / Pesanan Penjualan / PO) via Gmail atau SMTP.
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - docType: "sales-quotation" | "sales-order" | "purchase-order" | "sales-invoice"
 *  - docId: string
 *  - provider: "gmail" | "smtp" (default gmail)
 */
export function SendDocumentDialog({ open, onClose, docType, docId, provider = "gmail" }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ to: "", cc: "", subject: "", message: "" });
  const [files, setFiles] = useState([]);
  const [meta, setMeta] = useState({ number: "", filename: "" });
  const [templates, setTemplates] = useState([]);
  const [tplKey, setTplKey] = useState("formal");

  const addFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    const next = [...files];
    for (const f of picked) {
      if (next.length >= 5) { toast.error("Maksimal 5 lampiran tambahan"); break; }
      if (f.size > 10 * 1024 * 1024) { toast.error(`File '${f.name}' melebihi 10MB`); continue; }
      next.push(f);
    }
    setFiles(next);
    e.target.value = "";
  };

  useEffect(() => {
    if (!open || !docId || !docType) return;
    setFiles([]);
    setLoading(true);
    Promise.all([
      api.get(`/send-document/preview`, { params: { doc_type: docType, doc_id: docId } }),
      api.get(`/send-document/templates`, { params: { doc_type: docType, doc_id: docId } }).catch(() => ({ data: { templates: [] } })),
    ])
      .then(([r, rt]) => {
        setForm({ to: r.data.to_email || "", cc: r.data.cc || "", subject: r.data.subject || "", message: r.data.message || "" });
        setMeta({ number: r.data.number, filename: r.data.filename });
        setTemplates(rt.data?.templates || []);
        setTplKey("formal");
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Gagal memuat preview"))
      .finally(() => setLoading(false));
  }, [open, docId, docType]);

  const applyTemplate = (key) => {
    setTplKey(key);
    const t = templates.find((x) => x.key === key);
    if (t) setForm((f) => ({ ...f, subject: t.subject, message: t.message }));
  };

  const submit = async () => {
    if (!form.to) { toast.error("Email tujuan wajib diisi"); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("doc_type", docType);
      fd.append("doc_id", docId);
      fd.append("to_email", form.to);
      fd.append("cc", form.cc);
      fd.append("subject", form.subject);
      fd.append("message", form.message);
      files.forEach((f) => fd.append("files", f));
      const token = localStorage.getItem("nusa_token");
      const resp = await fetch(`${API}/send-document/${provider}`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      const j = await resp.json();
      if (!resp.ok) throw new Error(j.detail || "Gagal kirim");
      toast.success(`Dokumen terkirim via ${provider === "smtp" ? "Email SMTP" : "Gmail"} ke ${j.to}`);
      setFiles([]);
      onClose();
    } catch (e) {
      const msg = e.message || "Gagal kirim";
      if (String(msg).includes("belum terhubung") || String(msg).includes("belum dikonfigurasi")) {
        toast.error(`${provider === "smtp" ? "SMTP" : "Gmail"} belum dikonfigurasi. Buka Pengaturan → Integrasi.`);
      } else {
        toast.error(msg);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0">
        <DialogHeader className="shrink-0 border-b border-slate-200 px-6 pt-6 pb-3">
          <DialogTitle>Kirim {meta.number ? `#${meta.number}` : "Dokumen"} via {provider === "smtp" ? "Email SMTP Anda" : "Gmail Anda"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 min-h-0 overflow-y-auto px-6 pt-4 pb-6">
          {loading ? (
            <p className="text-sm text-slate-500 py-6 text-center">Memuat preview…</p>
          ) : (
            <>
              {templates.length > 0 && (
                <div>
                  <Label>Template Email</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1" data-testid="send-doc-templates">
                    {templates.map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => applyTemplate(t.key)}
                        title={t.description}
                        data-testid={`send-doc-tpl-${t.key}`}
                        className={`text-xs px-2.5 py-1 rounded border transition-colors ${tplKey === t.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">Klik template untuk mengganti subjek &amp; isi email — Anda bisa menyunting setelahnya.</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Kepada (Email)</Label>
                  <Input value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} data-testid="send-doc-to" />
                </div>
                <div>
                  <Label>CC (opsional)</Label>
                  <Input value={form.cc} onChange={(e) => setForm({ ...form, cc: e.target.value })} data-testid="send-doc-cc" />
                </div>
                <p className="col-span-2 text-[11px] text-slate-500 -mt-1" data-testid="send-doc-multi-hint">Lebih dari satu alamat email? Pisahkan dengan titik koma (;) — contoh: a@email.com; b@email.com</p>
              </div>
              <div>
                <Label>Subjek</Label>
                <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="send-doc-subject" />
              </div>
              <div>
                <Label>Isi Email</Label>
                <Textarea rows={9} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="send-doc-message" />
              </div>
              <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2 text-slate-600">
                Lampiran PDF: <b>{meta.filename}</b>
              </div>
              <div>
                <Label>File Kontrak / Lampiran Tambahan (opsional, maks 5 × 10MB)</Label>
                <input type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls,.zip" onChange={addFiles} data-testid="send-doc-file-input" className="block w-full text-xs text-slate-600 file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border file:border-slate-200 file:bg-slate-50 file:text-xs hover:file:bg-slate-100" />
                {files.length > 0 && (
                  <div className="mt-1.5 space-y-1" data-testid="send-doc-file-list">
                    {files.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1">
                        <span className="flex-1 truncate">{f.name} <span className="text-slate-400">({(f.size / 1024).toFixed(0)} KB)</span></span>
                        <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-rose-500 hover:text-rose-700" data-testid={`send-doc-file-remove-${i}`}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <div className="sticky bottom-0 z-10 -mx-6 -mb-6 mt-2 border-t border-slate-200 bg-background px-6 py-3">
          <Button onClick={submit} disabled={sending || !form.to} className={`w-full ${provider === "smtp" ? "bg-orange-600 hover:bg-orange-700" : "bg-red-600 hover:bg-red-700"}`} data-testid="send-doc-submit">
            {sending ? "Mengirim..." : `Kirim via ${provider === "smtp" ? "SMTP" : "Gmail"}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SendDocumentDialog;
